import React, { createContext, useContext } from 'react';
import clsx from 'clsx';
import { startOfMonth } from 'date-fns';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';

import Button from '@dashboard/components/Button';
import CreateWithAIButton from '@dashboard/components/CreateWithAIButton';
import StatisticsRangeControls from '@dashboard/components/StatisticsRangeControls';
import useStatisticsRange from '@dashboard/hooks/useStatisticsRange';
import PageHeader from '@dashboard/components/PageHeader';

import RecentPayments from './components/RecentPayments';
import RecentSubscriptions from './components/RecentSubscriptions';
import RecentBookings from './components/RecentBookings';
import RecentCustomers from './components/RecentCustomers';
import ActiveSubscribers from './components/ActiveSubscribers';
import NewSales from './components/NewSales';
import Views from './components/Views';
import MonthlyRecurringRevenue from './components/MonthlyRecurringRevenue';
import Revenue from './components/Revenue.js';
import RecentSubmissions from './components/RecentSubmissions';
import WhatsNew from './components/WhatsNew';
import CartRecovery from './components/CartRecovery';
import { StatHeader, StatMetric, StatBody, ViewAllLink } from './components/StatCard';
import checkHasStatisticsData from './checkHasStatisticsData';

const StatisticsContext = createContext();

export const useStatisticsContext = () => useContext(StatisticsContext);

export default function StatisticsDashboard() {
  const { seller } = useAuth();

  const statisticsRange = useStatisticsRange({
    storageKey: 'statistics',
    allTimeStart: seller?.createdAt ? startOfMonth(new Date(seller.createdAt)) : null,
    billingPeriod: {
      start: seller?.stripeSubscriptionCurrentPeriodStart,
      end: seller?.stripeSubscriptionCurrentPeriodEnd,
    },
  });

  return (
    <StatisticsContext.Provider
      value={{ hasStatisticsData: checkHasStatisticsData(seller), ...statisticsRange }}
    >
      <Dashboard />
    </StatisticsContext.Provider>
  );
}

function Dashboard() {
  const { seller, hasActiveSubscription } = useAuth();
  const statisticsRange = useStatisticsContext();

  return (
    <>
      <div className="p-4 space-y-4">
        <PageHeader
          title="Your overview"
          topBarActions={
            <div className="flex items-center gap-2">
              <CreateWithAIButton />

              <Button href="/pages/new" appearance="primary" className="whitespace-nowrap">
                Create page
              </Button>
            </div>
          }
        />

        <WhatsNew />

        <StatisticsRangeControls
          statisticsRange={statisticsRange}
          hasSubscription={hasActiveSubscription}
          billingPeriodStart={
            seller?.stripeSubscriptionCurrentPeriodStart
              ? new Date(seller.stripeSubscriptionCurrentPeriodStart)
              : null
          }
          billingPeriodEnd={
            seller?.stripeSubscriptionCurrentPeriodEnd
              ? new Date(seller.stripeSubscriptionCurrentPeriodEnd)
              : null
          }
        />

        <div className="overflow-hidden rounded-md border border-input">
          <div className="grid grid-cols-1 xl:grid-cols-6 2xl:grid-cols-9 -mr-px -mb-px">
            <StatisticsCard type="chart">
              <Revenue />
            </StatisticsCard>

            <StatisticsCard type="chart">
              <MonthlyRecurringRevenue />
            </StatisticsCard>

            <StatisticsCard type="chart">
              <ActiveSubscribers />
            </StatisticsCard>

            <StatisticsCard type="chart">
              <NewSales />
            </StatisticsCard>

            <StatisticsCard type="chart">
              <Views />
            </StatisticsCard>

            <StatisticsCard type="chart">
              <CartRecovery />
            </StatisticsCard>

            <StatisticsCard type="list">
              <StatHeader action={<ViewAllLink href="/payments" />}>
                <StatMetric label="New payments" />
              </StatHeader>
              <StatBody>
                <RecentPayments />
              </StatBody>
            </StatisticsCard>

            <StatisticsCard type="list">
              <StatHeader action={<ViewAllLink href="/subscriptions" />}>
                <StatMetric label="New subscriptions" />
              </StatHeader>
              <StatBody>
                <RecentSubscriptions />
              </StatBody>
            </StatisticsCard>

            <StatisticsCard type="list">
              <StatHeader action={<ViewAllLink href="/bookings" />}>
                <StatMetric label="New bookings" />
              </StatHeader>
              <StatBody>
                <RecentBookings />
              </StatBody>
            </StatisticsCard>

            <StatisticsCard type="list">
              <StatHeader action={<ViewAllLink href="/submissions" />}>
                <StatMetric label="New submissions" />
              </StatHeader>
              <StatBody>
                <RecentSubmissions />
              </StatBody>
            </StatisticsCard>

            <StatisticsCard type="list">
              <StatHeader action={<ViewAllLink href="/customers" />}>
                <StatMetric label="New customers" />
              </StatHeader>
              <StatBody>
                <RecentCustomers />
              </StatBody>
            </StatisticsCard>
          </div>
        </div>
      </div>
    </>
  );
}

function StatisticsCard({ children, type = 'chart' }) {
  const { hasStatisticsData } = useStatisticsContext();
  const { isAuthenticating } = useAuth();

  if (!hasStatisticsData && type === 'chart' && !isAuthenticating) {
    return (
      <div
        className={clsx(
          'min-w-0 xl:col-span-3 border-b border-r border-input px-8 pt-8 pb-1 relative',
        )}
      >
        <div className="absolute inset-0 bg-white dark:bg-background opacity-75 z-10 flex items-center justify-center">
          <div className="text-center text-gray-700 dark:text-muted-foreground text-sm">
            Start a subscription to see your statistics
          </div>
        </div>

        {children}
      </div>
    );
  }

  return (
    <div className={clsx('min-w-0 xl:col-span-3 border-b border-r border-input px-8 pt-8 pb-1')}>
      {children}
    </div>
  );
}
